'use client';
import { useCurrentEditor } from '@tiptap/react';
import {
  BoldIcon,
  ItalicIcon,
  StrikethroughIcon,
  UndoIcon,
  RedoIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  Heading4Icon,
  ListIcon,
  ListOrderedIcon,
  SquareDashedBottomCodeIcon as CodeBlockIcon,
  TextQuoteIcon,
  MinusIcon as DividerIcon,
} from 'lucide-react';

import { Control, SizeControl } from '@/types/editor';
import ControlButton from './ControlButton';

const MenuBar = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  const historyControls: Control[] = [
    {
      id: 'undo',
      icon: UndoIcon,
      tooltip: 'Undo',
      command: () => editor.chain().focus().undo().run(),
      disabled: !editor.can().chain().focus().undo().run(),
    },
    {
      id: 'redo',
      icon: RedoIcon,
      tooltip: 'Redo',
      command: () => editor.chain().focus().redo().run(),
      disabled: !editor.can().chain().focus().redo().run(),
    },
  ];

  const styleControls: Control[] = [
    {
      id: 'bold',
      icon: BoldIcon,
      tooltip: 'Bold',
      command: () => editor.chain().focus().toggleBold().run(),
      disabled: !editor.can().chain().focus().toggleBold().run(),
    },
    {
      id: 'italic',
      icon: ItalicIcon,
      tooltip: 'Italic',
      command: () => editor.chain().focus().toggleItalic().run(),
      disabled: !editor.can().chain().focus().toggleItalic().run(),
    },
    {
      id: 'strikethrough',
      icon: StrikethroughIcon,
      tooltip: 'Strikethrough',
      command: () => editor.chain().focus().toggleStrike().run(),
      disabled: !editor.can().chain().focus().toggleStrike().run(),
    },
  ];

  const sizeControls: SizeControl[] = [
    {
      id: 'h1',
      level: 1,
      icon: Heading1Icon,
      tooltip: 'Heading 1',
      command: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    },
    {
      id: 'h2',
      level: 2,
      icon: Heading2Icon,
      tooltip: 'Heading 2',
      command: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      id: 'h3',
      level: 3,
      icon: Heading3Icon,
      tooltip: 'Heading 3',
      command: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      id: 'h4',
      level: 4,
      icon: Heading4Icon,
      tooltip: 'Heading 4',
      command: () => editor.chain().focus().toggleHeading({ level: 4 }).run(),
    },
  ];

  const listControls: Control[] = [
    {
      id: 'bulletList',
      icon: ListIcon,
      tooltip: 'List',
      command: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      id: 'orderedList',
      icon: ListOrderedIcon,
      tooltip: 'Numbered List',
      command: () => editor.chain().focus().toggleOrderedList().run(),
    },
  ];

  const blockControls: Control[] = [
    {
      id: 'codeBlock',
      icon: CodeBlockIcon,
      tooltip: 'Code Block',
      command: () => editor.chain().focus().toggleCodeBlock().run(),
    },
    {
      id: 'blockquote',
      icon: TextQuoteIcon,
      tooltip: 'Blockquote',
      command: () => editor.chain().focus().toggleBlockquote().run(),
    },
    {
      id: 'horizontalRule',
      icon: DividerIcon,
      tooltip: 'Divider',
      command: () => editor.chain().focus().setHorizontalRule().run(),
    },
  ];

  // TODO: Implement manual save button functionality

  return (
    <div className='mb-4 card card-border shadow-md p-3 overflow-x-auto'>
      <div className='flex flex-row gap-2'>
        {historyControls.map(control => (
          <ControlButton
            key={`chenook-editor-history-control-${control.id}`}
            control={control}
            editor={editor}
          />
        ))}
        <div className='divider divider-horizontal m-0' />
        {styleControls.map(control => (
          <ControlButton
            key={`chenook-editor-style-control-${control.id}`}
            control={control}
            editor={editor}
          />
        ))}
        <div className='divider divider-horizontal m-0' />
        {sizeControls.map(control => (
          <ControlButton
            key={`chenook-editor-size-control-${control.id}`}
            control={control}
            editor={editor}
            className={
              editor?.isActive('heading', { level: control.level })
                ? `btn btn-sm btn-accent`
                : `btn btn-sm`
            }
          />
        ))}
        <div className='divider divider-horizontal m-0' />
        {listControls.map(control => (
          <ControlButton
            key={`chenook-editor-list-control-${control.id}`}
            control={control}
            editor={editor}
          />
        ))}
        <div className='divider divider-horizontal m-0' />
        {blockControls.map(control => (
          <ControlButton
            key={`chenook-editor-list-control-${control.id}`}
            control={control}
            editor={editor}
          />
        ))}
      </div>
    </div>
  );
};

export default MenuBar;
